import PropTypes from 'prop-types';
import { useCallback, useEffect, useRef } from 'react';
import './Modal.css';

const SIZE_MAX_WIDTH = {
  small: 480,
  default: 540,
  large: 640,
  xlarge: 720,
};

const FOCUSABLE =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

export function Modal({
  open,
  onClose,
  size = 'default',
  closeOnBackdrop = true,
  flat = false,
  children,
}) {
  const cardRef = useRef(null);
  const prevFocusRef = useRef(null);

  // onClose can be a fresh arrow on every parent render — keep a stable ref
  // so the keydown effect doesn't re-run (which would re-trigger the focus
  // RAF and steal focus from the user's input on every keystroke).
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  const handleBackdropClick = useCallback(
    (e) => {
      if (closeOnBackdrop && e.target === e.currentTarget) {
        onCloseRef.current?.();
      }
    },
    [closeOnBackdrop],
  );

  // Focus + keydown bound only to `open` — runs once when the modal opens,
  // tears down once when it closes. Re-renders during input don't re-run.
  useEffect(() => {
    if (!open) return undefined;
    prevFocusRef.current = document.activeElement;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        // Swallow Escape so only the topmost layer closes. Other document-level
        // Escape listeners underneath (e.g. RightSidebar) listen in the bubble
        // phase; intercepting in the CAPTURE phase + stopPropagation prevents
        // the event from reaching them, so the modal closes without also
        // closing the drawer behind it.
        e.stopPropagation();
        onCloseRef.current?.();
      }
    };
    document.addEventListener('keydown', handleKeyDown, true);
    const raf = requestAnimationFrame(() => {
      // Focus the first non-close element so users can start typing
      // immediately. The close button is `.modal-close` and is intentionally
      // skipped to avoid stealing focus from the form on every render.
      const focusables = cardRef.current?.querySelectorAll(FOCUSABLE) ?? [];
      const first = Array.from(focusables).find(
        (el) => !el.classList.contains('modal-close'),
      );
      (first ?? focusables[0])?.focus();
    });
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('keydown', handleKeyDown, true);
      prevFocusRef.current?.focus();
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className={`modal-backdrop${flat ? ' modal-backdrop--flat' : ''}`}
      onClick={handleBackdropClick}
      role="presentation"
    >
      <div
        className={`modal-card${flat ? ' modal-card--flat' : ''}`}
        role="dialog"
        aria-modal="true"
        style={{ maxWidth: SIZE_MAX_WIDTH[size] ?? SIZE_MAX_WIDTH.default }}
        ref={cardRef}
      >
        {children}
      </div>
    </div>
  );
}

function Header({ title, kicker, onClose }) {
  return (
    <div className="modal-head">
      {kicker ? (
        <div className="modal-head__stack">
          <div className="modal-kicker">{kicker}</div>
          <h3>{title}</h3>
        </div>
      ) : (
        <h3>{title}</h3>
      )}
      <button
        className="modal-close modal__x"
        onClick={onClose}
        aria-label="Cerrar"
      >
        ×
      </button>
    </div>
  );
}

function Body({ children, noPadding }) {
  return (
    <div className="modal-body" style={noPadding ? { padding: 0 } : undefined}>
      {children}
    </div>
  );
}

function Foot({ children }) {
  return <div className="modal-foot">{children}</div>;
}

Modal.Header = Header;
Modal.Body = Body;
Modal.Foot = Foot;

Modal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  size: PropTypes.oneOf(['small', 'default', 'large', 'xlarge']),
  closeOnBackdrop: PropTypes.bool,
  // Flat: no shadow and no blur, for surfaces whose kit forbids them (the
  // receptor portal). The default look is unchanged.
  flat: PropTypes.bool,
  children: PropTypes.node,
};

Header.propTypes = {
  title: PropTypes.string.isRequired,
  kicker: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

Body.propTypes = {
  children: PropTypes.node,
  noPadding: PropTypes.bool,
};

Foot.propTypes = {
  children: PropTypes.node,
};

export default Modal;
